import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FC } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
    LuX, LuCopy, LuCheck, LuSmartphone, LuMonitor, LuDownload, LuCircleCheck, LuMonitorDown, LuEllipsisVertical,
    LuEllipsis, LuAppWindow, LuPin, LuMousePointerClick,
} from 'react-icons/lu';
import { instalar, ouvirInstalacao, podeInstalarComUmToque, rodandoInstalado } from '../lib/instalarApp';
import './InstalarBrik.css';

interface Props {
    aberto: boolean;
    /** Quem abriu: a janela se põe ao lado da sidebar, na altura dele, e o foco volta para ele. */
    ancora: HTMLElement | null;
    onClose: () => void;
}

type Navegador = 'chrome' | 'edge' | 'safari' | 'outro';

const navegador = (): Navegador => {
    const ua = navigator.userAgent;
    if (/Edg\//.test(ua)) return 'edge';
    if (/Chrome\//.test(ua) && !/OPR\//.test(ua)) return 'chrome';
    if (/Safari\//.test(ua) && !/Chrome|Chromium/.test(ua)) return 'safari';
    return 'outro';
};

/**
 * O "Instalar" da sidebar: uma janela, dois caminhos lado a lado.
 * - Celular: o QR Code que leva ao /instalar (no computador aquela página só manda abrir
 *   no celular, então a porta mostra o caminho em vez de ir até lá). O endereço é o do
 *   próprio ambiente: em HML o QR leva ao HML.
 * - Computador: o Brik como app, em janela própria. Chrome e Edge instalam em um clique
 *   quando avisaram que podem (`lib/instalarApp`); senão, o caminho pelo menu do navegador.
 *
 * Por portal em `document.body`: o `.sidebar-slot` tem empilhamento próprio e prenderia
 * a janela embaixo do conteúdo da tela.
 */
const InstalarBrik: FC<Props> = ({ aberto, ancora, onClose }) => {
    const url = `${window.location.origin}/instalar`;
    const ref = useRef<HTMLDivElement>(null);
    const refFechar = useRef<HTMLButtonElement>(null);
    const [pos, setPos] = useState<CSSProperties>({});
    const [copiado, setCopiado] = useState(false);
    const [umClique, setUmClique] = useState(podeInstalarComUmToque);
    const [instalado, setInstalado] = useState(rodandoInstalado);

    /* O "pode instalar" do navegador chega quando quer; a instalação pode vir pelo ícone
       da barra de endereço com a janela aberta. As duas coisas mudam o lado direito. */
    useEffect(() => ouvirInstalacao(() => setUmClique(podeInstalarComUmToque())), []);
    useEffect(() => {
        const aoInstalar = () => setInstalado(true);
        window.addEventListener('appinstalled', aoInstalar);
        return () => window.removeEventListener('appinstalled', aoInstalar);
    }, []);

    /* Ao lado da sidebar, alinhada a quem abriu: na metade de baixo da tela (o
       rodapé) cresce para cima; na de cima, para baixo. */
    useLayoutEffect(() => {
        if (!aberto || !ancora) return;
        const a = ancora.getBoundingClientRect();
        const lateral = ancora.closest('.sidebar')?.getBoundingClientRect().right ?? a.right;
        setPos(a.top < window.innerHeight / 2
            ? { left: lateral + 10, top: Math.max(8, a.top) }
            : { left: lateral + 10, bottom: Math.max(8, window.innerHeight - a.bottom) });
    }, [aberto, ancora]);

    useEffect(() => {
        if (!aberto) return;
        setCopiado(false);
        refFechar.current?.focus();
        const fechar = () => { onClose(); ancora?.focus(); };
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
        const aoClicar = (e: MouseEvent) => {
            const alvo = e.target as Node;
            if (!ref.current?.contains(alvo) && !ancora?.contains(alvo)) onClose();
        };
        document.addEventListener('keydown', aoTeclar);
        document.addEventListener('mousedown', aoClicar);
        return () => {
            document.removeEventListener('keydown', aoTeclar);
            document.removeEventListener('mousedown', aoClicar);
        };
    }, [aberto, ancora, onClose]);

    if (!aberto) return null;

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch {
            /* Sem permissão de área de transferência: o endereço está escrito logo acima. */
        }
    };

    const instalarAqui = async () => {
        if (await instalar()) setInstalado(true);
    };

    const nav = navegador();

    return createPortal(
        <div ref={ref} className="inc" style={pos} role="dialog" aria-labelledby="inc-titulo">
            <div className="inc-topo">
                <h2 id="inc-titulo" className="inc-titulo">Instalar o Brik</h2>
                <button ref={refFechar} type="button" className="inc-fechar" onClick={onClose} aria-label="Fechar">
                    <LuX />
                </button>
            </div>

            <div className="inc-colunas">
                <section className="inc-coluna" aria-labelledby="inc-celular">
                    <h3 id="inc-celular" className="inc-sub"><LuSmartphone /> No celular</h3>
                    <p className="inc-texto">
                        Aponte a câmera do celular para instalar o app e receber um aviso a cada lead novo.
                    </p>

                    <div className="inc-qr">
                        <QRCodeSVG value={url} size={148} level="M" />
                    </div>

                    <div className="inc-link">
                        <span className="inc-url">{url.replace(/^https?:\/\//, '')}</span>
                        <button type="button" className="inc-botao" onClick={copiar}>
                            {copiado ? <LuCheck /> : <LuCopy />}
                            <span>{copiado ? 'Copiado' : 'Copiar link'}</span>
                        </button>
                    </div>
                    <p className="inc-nota">O mesmo link serve para mandar à equipe pelo WhatsApp.</p>
                </section>

                <div className="inc-filete" aria-hidden="true" />

                <section className="inc-coluna" aria-labelledby="inc-computador">
                    <h3 id="inc-computador" className="inc-sub"><LuMonitor /> No computador</h3>
                    <p className="inc-texto">Como um programa do computador.</p>

                    <ul className="inc-ganhos">
                        <li><LuAppWindow /> Janela própria, fora das abas do navegador</li>
                        <li><LuPin /> Ícone na barra de tarefas e na área de trabalho</li>
                        <li><LuMousePointerClick /> Abre direto no Brik, sem procurar a aba</li>
                    </ul>

                    {/* Embaixo, na mesma linha do "Copiar link": a ação de cada lado fica no
                        mesmo lugar, e o meio da coluna leva o que muda para quem instala. */}
                    <div className="inc-acao">
                    {instalado ? (
                        <div className="inc-ok" role="status">
                            <LuCircleCheck />
                            <span>Instalado neste computador. Abra pelo ícone do Brik na área de trabalho ou no menu Iniciar.</span>
                        </div>
                    ) : umClique ? (
                        <>
                            <button type="button" className="inc-botao is-primario" onClick={instalarAqui}>
                                <LuDownload />
                                <span>Instalar no computador</span>
                            </button>
                            <p className="inc-nota">Leva um clique e pode ser desinstalado quando quiser.</p>
                        </>
                    ) : nav === 'chrome' || nav === 'edge' ? (
                        <>
                            <ol className="inc-passos">
                                <li>
                                    Clique no ícone de instalar <LuMonitorDown className="inc-icone" aria-label="ícone de instalar" />,
                                    no canto direito da barra de endereço.
                                </li>
                                {nav === 'chrome' ? (
                                    <li>
                                        Sem o ícone: menu <LuEllipsisVertical className="inc-icone" aria-label="três pontos" /> →{' '}
                                        <strong>Transmitir, salvar e compartilhar</strong> → <strong>Instalar página como app</strong>.
                                    </li>
                                ) : (
                                    <li>
                                        Sem o ícone: menu <LuEllipsis className="inc-icone" aria-label="três pontos" /> →{' '}
                                        <strong>Aplicativos</strong> → <strong>Instalar este site como um aplicativo</strong>.
                                    </li>
                                )}
                            </ol>
                            <p className="inc-nota">Já instalou? O Brik abre pelo ícone na área de trabalho ou no menu Iniciar.</p>
                        </>
                    ) : nav === 'safari' ? (
                        <ol className="inc-passos">
                            <li>No Safari, abra o menu <strong>Arquivo</strong>.</li>
                            <li>Clique em <strong>Adicionar ao Dock</strong>.</li>
                        </ol>
                    ) : (
                        <p className="inc-texto">
                            Este navegador não instala sites como app. Abra o Brik no <strong>Chrome</strong> ou no{' '}
                            <strong>Edge</strong> para instalar.
                        </p>
                    )}
                    </div>
                </section>
            </div>
        </div>,
        document.body,
    );
};

export default InstalarBrik;
